import { Navigate, type RouteObject } from "react-router-dom";
import { AuditPage } from "@/modules/audit/pages/AuditPage";
import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { ClientDetailPage } from "@/modules/clients/pages/ClientDetailPage";
import { ClientFormPage } from "@/modules/clients/pages/ClientFormPage";
import { ClientsPage } from "@/modules/clients/pages/ClientsPage";
import { ContractPage } from "@/modules/contracts/pages/ContractPage";
import { DashboardPage } from "@/modules/dashboard/pages/DashboardPage";
import { LeadsKanbanPage } from "@/modules/leads/pages/LeadsKanbanPage";
import { BlocksPage } from "@/modules/objects/pages/BlocksPage";
import { BuildingsPage } from "@/modules/objects/pages/BuildingsPage";
import { RoomCreatePage } from "@/modules/objects/pages/RoomCreatePage";
import { RoomDetailPage } from "@/modules/objects/pages/RoomDetailPage";
import { RoomsMapPage } from "@/modules/objects/pages/RoomsMapPage";
import { SectionsPage } from "@/modules/objects/pages/SectionsPage";
import { OrganizationDetailPage } from "@/modules/organizations/pages/OrganizationDetailPage";
import { OrganizationFormPage } from "@/modules/organizations/pages/OrganizationFormPage";
import { OrganizationsPage } from "@/modules/organizations/pages/OrganizationsPage";
import { BankAccountPage } from "@/modules/payments/pages/BankAccountPage";
import { CancelledSalePaymentsPage } from "@/modules/payments/pages/CancelledSalePaymentsPage";
import { DiscountScalePage } from "@/modules/payments/pages/DiscountScalePage";
import { EarlyPaymentPage } from "@/modules/payments/pages/EarlyPaymentPage";
import { PaymentCreatePage } from "@/modules/payments/pages/PaymentCreatePage";
import { PaymentSchedulesPage } from "@/modules/payments/pages/PaymentSchedulesPage";
import { PaymentsPage } from "@/modules/payments/pages/PaymentsPage";
import { ProfilePage } from "@/modules/profile/pages/ProfilePage";
import { DebtsReportPage } from "@/modules/reports/pages/DebtsReportPage";
import { ManagersRatingPage } from "@/modules/reports/pages/ManagersRatingPage";
import { PaymentReportPage } from "@/modules/reports/pages/PaymentReportPage";
import { RoomsReportPage } from "@/modules/reports/pages/RoomsReportPage";
import { SalesReportPage } from "@/modules/reports/pages/SalesReportPage";
import { PaymentSchedulePage } from "@/modules/sales/pages/PaymentSchedulePage";
import { SaleCreatePage } from "@/modules/sales/pages/SaleCreatePage";
import { SaleDetailPage } from "@/modules/sales/pages/SaleDetailPage";
import { SalesPage } from "@/modules/sales/pages/SalesPage";
import { SmsAutoSettingsPage } from "@/modules/sms/pages/SmsAutoSettingsPage";
import { SmsBalansPage } from "@/modules/sms/pages/SmsBalansPage";
import { SmsDetalizationPage } from "@/modules/sms/pages/SmsDetalizationPage";
import { SmsLogsPage } from "@/modules/sms/pages/SmsLogsPage";
import { SmsReportsPage } from "@/modules/sms/pages/SmsReportsPage";
import { SmsSendPage } from "@/modules/sms/pages/SmsSendPage";
import { SmsTemplatesPage } from "@/modules/sms/pages/SmsTemplatesPage";
import { RolesPage } from "@/modules/users/pages/RolesPage";
import { UsersPage } from "@/modules/users/pages/UsersPage";
import { AuthLayout } from "@/shared/components/layouts/AuthLayout";
import { MainLayout } from "@/shared/components/layouts/MainLayout";

export const routes: RouteObject[] = [
	// Public routes - Auth
	{
		path: "/login",
		element: <AuthLayout />,
		children: [
			{
				index: true,
				element: <LoginPage />,
			},
		],
	},

	// Protected routes
	{
		path: "/",
		element: <MainLayout />,
		children: [
			{
				index: true,
				element: <Navigate to="/dashboard" replace />,
			},
			{
				path: "dashboard",
				element: <DashboardPage />,
			},
			{
				path: "organizations",
				element: <OrganizationsPage />,
			},
			{
				path: "organizations/new",
				element: <OrganizationFormPage />,
			},
			{
				path: "organizations/:organizationId/edit",
				element: <OrganizationFormPage />,
			},
			{
				path: "organizations/:organizationId",
				element: <OrganizationDetailPage />,
			},
			{
				path: "objects",
				element: <BuildingsPage />,
			},
			{
				path: "objects/:buildingId/blocks",
				element: <BlocksPage />,
			},
			{
				path: "objects/:buildingId/blocks/:blockId/sections",
				element: <SectionsPage />,
			},
			{
				path: "rooms",
				element: <RoomsMapPage />,
			},
			{
				path: "rooms/:roomId",
				element: <RoomDetailPage />,
			},
			{
				path: "rooms/new",
				element: <RoomCreatePage />,
			},
			{
				path: "clients",
				element: <ClientsPage />,
			},
			{
				path: "clients/new",
				element: <ClientFormPage />,
			},
			{
				path: "clients/:clientId",
				element: <ClientDetailPage />,
			},
			{
				path: "clients/:clientId/edit",
				element: <ClientFormPage />,
			},
			{
				path: "leads",
				element: <LeadsKanbanPage />,
			},
			{
				path: "sales",
				element: <SalesPage />,
			},
			{
				path: "sales/new",
				element: <SaleCreatePage />,
			},
			{
				path: "sales/:saleId",
				element: <SaleDetailPage />,
			},
			{
				path: "sales/:saleId/schedule",
				element: <PaymentSchedulePage />,
			},
			{
				path: "payments",
				element: <PaymentsPage />,
			},
			{
				path: "payments/new",
				element: <PaymentCreatePage />,
			},
			{
				path: "payments/early",
				element: <EarlyPaymentPage />,
			},
			{
				path: "payments/overdue",
				element: <PaymentSchedulesPage />,
			},
			{
				path: "payments/cancelled",
				element: <CancelledSalePaymentsPage />,
			},
			{
				path: "payments/bank",
				element: <BankAccountPage />,
			},
			{
				path: "payments/discount-scale",
				element: <DiscountScalePage />,
			},
			{
				path: "sms/balance",
				element: <SmsBalansPage />,
			},
			{
				path: "sms/templates",
				element: <SmsTemplatesPage />,
			},
			{
				path: "sms/auto",
				element: <SmsAutoSettingsPage />,
			},
			{
				path: "sms/send",
				element: <SmsSendPage />,
			},
			{
				path: "sms/batch",
				element: <SmsSendPage />,
			},
			{
				path: "sms/logs",
				element: <SmsLogsPage />,
			},
			{
				path: "sms/reports",
				element: <SmsReportsPage />,
			},
			{
				path: "sms/detalization",
				element: <SmsDetalizationPage />,
			},
			{
				path: "reports/sales",
				element: <SalesReportPage />,
			},
			{
				path: "reports/debts",
				element: <DebtsReportPage />,
			},
			{
				path: "reports/payments",
				element: <PaymentReportPage />,
			},
			{
				path: "reports/managers",
				element: <ManagersRatingPage />,
			},
			{
				path: "reports/rooms",
				element: <RoomsReportPage />,
			},
			{
				path: "users",
				element: <UsersPage />,
			},
			{
				path: "roles",
				element: <RolesPage />,
			},
			{
				path: "audit",
				element: <AuditPage />,
			},
			{
				path: "profile",
				element: <ProfilePage />,
			},
			{
				path: "contract",
				element: <ContractPage />,
			},
		],
	},

	// 404 - Redirect to dashboard or login
	{
		path: "*",
		element: <Navigate to="/" replace />,
	},
];
